/**
 * Card de milestones del proyecto. Lista milestones por target_date con
 * barra de progreso. Expandible: muestra épicas con su progreso. Cada
 * épica expandible: fetch live de sub-items desde Plane.
 */
import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  ChevronRight,
  ExternalLink,
  RefreshCw,
  Calendar,
  CheckCircle2,
  Circle,
  XCircle,
  PauseCircle,
} from "lucide-react";
import clsx from "clsx";
import type { ProjectMilestone, MilestoneEpic } from "@axorial/shared";
import { api } from "@/lib/api";
import { formatDate } from "@/lib/format";

interface MilestoneWithEpics extends ProjectMilestone {
  epics: MilestoneEpic[];
}

interface MilestoneItem {
  id: string;
  name: string;
  state: string | null;
  stateGroup: string | null;
  updatedAt: string | null;
  url: string;
}

const STATUS_BADGE: Record<string, string> = {
  backlog: "badge-ghost",
  planned: "badge-info",
  active: "badge-primary",
  completed: "badge-success",
  cancelled: "badge-error",
};

const STATUS_LABEL: Record<string, string> = {
  backlog: "Backlog",
  planned: "Planeado",
  active: "Activo",
  completed: "Completado",
  cancelled: "Cancelado",
};

export function MilestonesCard({
  projectId,
  hasPlaneLink,
  canSync,
}: {
  projectId: string;
  hasPlaneLink: boolean;
  canSync: boolean;
}) {
  const qc = useQueryClient();
  const [expanded, setExpanded] = useState<Set<string>>(new Set());

  const { data, isLoading } = useQuery<{
    milestones: MilestoneWithEpics[];
    lastSyncedAt: number | null;
  }>({
    queryKey: ["milestones", projectId],
    queryFn: () => api.get(`/projects/${projectId}/milestones`),
    enabled: hasPlaneLink,
  });

  const sync = useMutation({
    mutationFn: () => api.post(`/projects/${projectId}/milestones/sync`),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["milestones", projectId] }),
  });

  const toggle = (id: string) => {
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  if (!hasPlaneLink) {
    return (
      <p className="text-sm text-base-content/55 italic">
        Vincula este proyecto con Plane para ver sus milestones aquí.
      </p>
    );
  }

  if (isLoading) {
    return (
      <div className="grid place-items-center py-6">
        <span className="loading loading-spinner text-primary" />
      </div>
    );
  }

  const milestones = data?.milestones ?? [];

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between gap-2">
        <div className="text-xs text-base-content/55">
          {data?.lastSyncedAt
            ? `Última sincronización: ${formatRelative(data.lastSyncedAt)}`
            : "Sin sincronizar aún"}
        </div>
        {canSync && (
          <button
            type="button"
            onClick={() => sync.mutate()}
            disabled={sync.isPending}
            className="btn btn-ghost btn-xs gap-1.5"
          >
            <RefreshCw className={clsx("size-3", sync.isPending && "animate-spin")} />
            Sincronizar
          </button>
        )}
      </div>

      {milestones.length === 0 ? (
        <p className="text-sm text-base-content/55 italic">Sin milestones en Plane todavía.</p>
      ) : (
        <ul className="space-y-2 -mx-1">
          {milestones.map((m) => (
            <MilestoneRow
              key={m.id}
              milestone={m}
              isExpanded={expanded.has(m.id)}
              onToggle={() => toggle(m.id)}
            />
          ))}
        </ul>
      )}
    </div>
  );
}

// ─── Milestone row ────────────────────────────────────────────────────────

function MilestoneRow({
  milestone,
  isExpanded,
  onToggle,
}: {
  milestone: MilestoneWithEpics;
  isExpanded: boolean;
  onToggle: () => void;
}) {
  const hasEpics = milestone.epics.length > 0;
  const progress = milestone.progressPct;
  const statusBadge = STATUS_BADGE[milestone.status] ?? "badge-ghost";
  const statusLabel = STATUS_LABEL[milestone.status] ?? milestone.status;

  return (
    <li className="rounded-md border border-base-300/60 hover:border-base-300 transition-colors">
      <button
        type="button"
        onClick={onToggle}
        className="w-full flex items-start gap-3 px-3 py-3 text-left group"
      >
        <ChevronRight
          className={clsx(
            "size-4 mt-0.5 text-base-content/40 transition-transform shrink-0",
            isExpanded && "rotate-90",
          )}
        />
        <div className="min-w-0 flex-1 space-y-2">
          <div className="flex items-start justify-between gap-2">
            <div className="min-w-0 flex-1">
              <div className="font-medium text-sm truncate">{milestone.name}</div>
              <div className="text-xs text-base-content/55 mt-0.5 flex items-center gap-2 flex-wrap">
                <span className={`badge badge-xs ${statusBadge}`}>{statusLabel}</span>
                {milestone.targetDate && (
                  <span className="inline-flex items-center gap-1">
                    <Calendar className="size-3" /> {formatDate(milestone.targetDate)}
                  </span>
                )}
                <span>
                  {milestone.epicsCount} {milestone.epicsCount === 1 ? "épica" : "épicas"}
                </span>
              </div>
            </div>
            <div className="text-right shrink-0">
              {progress !== null ? (
                <div className="text-base font-semibold tabular-nums">{progress}%</div>
              ) : (
                <div className="text-xs text-base-content/40 italic">sin épicas</div>
              )}
            </div>
          </div>
          <ProgressBar progress={progress} status={milestone.status} />
        </div>
      </button>

      {isExpanded && (
        <div className="px-3 pb-3 pt-1 -mt-1 space-y-2 border-t border-base-300/40 ml-7">
          {hasEpics ? (
            milestone.epics.map((epic) => <EpicRow key={epic.id} epic={epic} />)
          ) : (
            <p className="text-xs text-base-content/55 italic mt-2">
              Sin épicas (issues con sub-issues) en este milestone. El milestone cuenta como
              progreso solo cuando tiene épicas ligadas.
            </p>
          )}
          <a
            href={milestone.planeUrl}
            target="_blank"
            rel="noreferrer"
            className="link link-primary text-xs inline-flex items-center gap-1 mt-1"
          >
            <ExternalLink className="size-3" /> Abrir milestone en Plane
          </a>
        </div>
      )}
    </li>
  );
}

// ─── Epic row ─────────────────────────────────────────────────────────────

function EpicRow({ epic }: { epic: MilestoneEpic }) {
  const [open, setOpen] = useState(false);
  return (
    <div className="rounded border border-base-300/40">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="w-full flex items-start gap-2 px-2.5 py-2 text-left group"
      >
        <ChevronRight
          className={clsx(
            "size-3.5 mt-0.5 text-base-content/40 transition-transform shrink-0",
            open && "rotate-90",
          )}
        />
        <div className="min-w-0 flex-1">
          <div className="text-sm font-medium truncate">{epic.name}</div>
          <div className="text-xs text-base-content/55 mt-0.5 flex items-center gap-2 flex-wrap">
            {epic.state && <span>{epic.state}</span>}
            <span className="tabular-nums">
              {epic.closedItems}/{epic.totalItems} items
            </span>
            <span className="font-medium tabular-nums text-base-content/70">
              {epic.progressPct}%
            </span>
          </div>
        </div>
        <div className="w-24 shrink-0 mt-1.5">
          <MiniBar progress={epic.progressPct} />
        </div>
      </button>

      {open && <EpicItems epic={epic} />}
    </div>
  );
}

function EpicItems({ epic }: { epic: MilestoneEpic }) {
  const { data, isLoading, error } = useQuery<{ items: MilestoneItem[] }>({
    queryKey: ["milestone-items", epic.id],
    queryFn: () => api.get(`/milestones/epics/${epic.id}/items`),
    staleTime: 30_000,
  });

  if (isLoading) {
    return (
      <div className="px-2.5 py-2 ml-5">
        <span className="loading loading-spinner loading-xs text-primary" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="px-2.5 py-2 ml-5 text-xs text-error">Error al cargar items desde Plane.</div>
    );
  }

  const items = data?.items ?? [];

  return (
    <ul className="px-2.5 pb-2.5 ml-5 space-y-0.5 border-t border-base-300/40 pt-1.5">
      <li className="text-xs text-base-content/55 mb-1">Sub-items (live desde Plane):</li>
      {items.length === 0 ? (
        <li className="text-xs text-base-content/55 italic">Sin sub-items.</li>
      ) : (
        items.map((it) => (
          <li
            key={it.id}
            className="flex items-center gap-2 text-xs py-1 px-1 rounded hover:bg-base-200/60"
          >
            <ItemStateIcon stateGroup={it.stateGroup} />
            <span className="flex-1 truncate">{it.name}</span>
            {it.updatedAt && (
              <span className="text-base-content/45 shrink-0">
                {formatRelative(Math.floor(new Date(it.updatedAt).getTime() / 1000))}
              </span>
            )}
            <a
              href={it.url}
              target="_blank"
              rel="noreferrer"
              className="text-base-content/40 hover:text-primary shrink-0"
              aria-label="Abrir en Plane"
            >
              <ExternalLink className="size-3" />
            </a>
          </li>
        ))
      )}
    </ul>
  );
}

// ─── Helpers ──────────────────────────────────────────────────────────────

function ItemStateIcon({ stateGroup }: { stateGroup: string | null }) {
  if (stateGroup === "completed")
    return <CheckCircle2 className="size-3.5 text-success shrink-0" />;
  if (stateGroup === "cancelled")
    return <XCircle className="size-3.5 text-base-content/30 shrink-0" />;
  if (stateGroup === "started") return <PauseCircle className="size-3.5 text-warning shrink-0" />;
  return <Circle className="size-3.5 text-base-content/30 shrink-0" />;
}

function ProgressBar({ progress, status }: { progress: number | null; status: string }) {
  const pct = progress ?? 0;
  const color =
    status === "completed"
      ? "bg-success"
      : status === "cancelled"
        ? "bg-error/50"
        : pct > 70
          ? "bg-success"
          : pct > 40
            ? "bg-warning"
            : pct > 0
              ? "bg-primary"
              : "bg-base-300";
  return (
    <div className="h-1.5 rounded-full bg-base-200 overflow-hidden">
      <div
        className={clsx("h-full transition-all", color)}
        style={{ width: `${progress === null ? 0 : pct}%` }}
      />
    </div>
  );
}

function MiniBar({ progress }: { progress: number }) {
  return (
    <div className="h-1 rounded-full bg-base-200 overflow-hidden">
      <div className="h-full bg-primary transition-all" style={{ width: `${progress}%` }} />
    </div>
  );
}

function formatRelative(unixSeconds: number): string {
  const ms = unixSeconds * 1000;
  const diffMs = Date.now() - ms;
  const minutes = Math.floor(diffMs / 60_000);
  if (minutes < 1) return "ahora";
  if (minutes < 60) return `hace ${minutes} min`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `hace ${hours} h`;
  const days = Math.floor(hours / 24);
  if (days < 30) return `hace ${days} d`;
  return formatDate(unixSeconds);
}
